import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

/**
 * Global Schema.org structured data: Organization + LocalBusiness. Mounted
 * once at AppShell level so every crawled page carries the business
 * identity in its head. Used by Google to populate the right-side knowledge
 * panel (Google Business Profile linkage) and the Local Pack listings.
 *
 * Why we keep this separate from <SEO>: per-page <SEO> already manages
 * title/canonical/OG. The organization block is identical across the entire
 * site — emitting it once at the shell level avoids duplicating the JSON
 * payload on every route render.
 */
export function OrganizationJsonLd() {
  const { t } = useTranslation();

  const data = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': 'https://faticaftan.com/#organization',
        name: 'Fati Caftan',
        url: 'https://faticaftan.com',
        logo: 'https://faticaftan.com/og-default.jpg',
        email: 'fatima@faticaftan.com',
        sameAs: [
          // Add Instagram / Facebook profile URLs here when the seller
          // creates them — Google uses sameAs to consolidate the brand
          // across the social graph.
        ],
        contactPoint: [
          {
            '@type': 'ContactPoint',
            telephone: '+212701048661',
            contactType: 'customer service',
            availableLanguage: ['fr', 'ar', 'en'],
          },
        ],
      },
      {
        '@type': 'LocalBusiness',
        '@id': 'https://faticaftan.com/#business',
        name: 'Fati Caftan',
        description: t('seo.organization.description'),
        url: 'https://faticaftan.com',
        image: 'https://faticaftan.com/og-default.jpg',
        telephone: '+212701048661',
        email: 'fatima@faticaftan.com',
        priceRange: 'MAD',
        currenciesAccepted: 'MAD',
        areaServed: {
          '@type': 'Country',
          name: 'Maroc',
        },
        // City + postal address get filled in once the seller picks a
        // public atelier address. Until then, omit rather than guess —
        // an empty/fake address harms local SEO more than no address.
      },
      {
        '@type': 'WebSite',
        '@id': 'https://faticaftan.com/#website',
        url: 'https://faticaftan.com',
        name: 'Fati Caftan',
        publisher: { '@id': 'https://faticaftan.com/#organization' },
        inLanguage: ['fr', 'ar', 'en'],
      },
    ],
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(data)}
      </script>
    </Helmet>
  );
}
